/**
 * HeroCodeSnippet — animated typing code widget and text-mask animation for the Hero section.
 *
 * Exports:
 *  - default (CodeSnippet) — the animated typing code window widget
 *  - MaskLine              — text reveal animation component for hero headline
 *  - line                  — framer-motion variant for MaskLine
 */
import { useState, useEffect, useRef } from "react";
import { motion, useAnimationControls } from "framer-motion";
import useMotionPreference from "@/hooks/useMotionPreference";

// ─── MaskLine animation variant ───────────────────────────────────────────────

export const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const MaskLine = ({ children, i }) => {
  const reduceMotion = useMotionPreference();
  const controls = useAnimationControls();
  const revealedImmediately = useRef(false);

  useEffect(() => {
    if (reduceMotion) {
      controls.stop();
      controls.set("show");
      revealedImmediately.current = true;
    } else if (!revealedImmediately.current) {
      controls.start("show");
    }
  }, [controls, reduceMotion]);

  return (
    <span className="block overflow-hidden pb-1">
      <motion.span custom={i} variants={line} initial={reduceMotion ? "show" : "hidden"} animate={controls} className="block">
        {children}
      </motion.span>
    </span>
  );
};

// ─── Code snippet data ────────────────────────────────────────────────────────

const SNIPPETS = [
  {
    title: "Server.java",
    lines: [
      [{ text: "@RestController", className: "text-purple-400" }],
      [{ text: "@RequestMapping", className: "text-purple-400" }, { text: '("/api/v1")', className: "text-green-400" }],
      [{ text: "public class", className: "text-blue-400" }, { text: " CoreService ", className: "text-yellow-200" }, { text: "{", className: "text-white" }],
      [{ text: "  @GetMapping", className: "text-purple-400" }, { text: '("/status")', className: "text-green-400" }],
      [{ text: "  public", className: "text-blue-400" }, { text: " ResponseEntity<String> ", className: "text-yellow-200" }, { text: "checkStatus", className: "text-blue-200" }, { text: "() {", className: "text-white" }],
      [{ text: "    return", className: "text-blue-400" }, { text: " ResponseEntity.", className: "text-white" }, { text: "ok", className: "text-blue-200" }, { text: '("All systems operational.");', className: "text-green-400" }],
      [{ text: "  }", className: "text-white" }],
      [{ text: "  // Optimizing algorithms...", className: "text-white/40 italic" }],
      [{ text: "  @PostMapping", className: "text-purple-400" }, { text: '("/deploy")', className: "text-green-400" }],
      [{ text: "  public void", className: "text-blue-400" }, { text: " shipIt", className: "text-blue-200" }, { text: "() {", className: "text-white" }],
      [{ text: "    deploymentService.", className: "text-white" }, { text: "execute", className: "text-blue-200" }, { text: "();", className: "text-white" }],
      [{ text: "  }", className: "text-white" }],
      [{ text: "}", className: "text-white" }],
    ]
  },
  {
    title: "App.tsx",
    lines: [
      [{ text: "import", className: "text-blue-400" }, { text: " { ", className: "text-white" }, { text: "useState", className: "text-yellow-200" }, { text: " } ", className: "text-white" }, { text: "from", className: "text-blue-400" }, { text: ' "react";', className: "text-green-400" }],
      [],
      [{ text: "export default function", className: "text-blue-400" }, { text: " App", className: "text-yellow-200" }, { text: "() {", className: "text-white" }],
      [{ text: "  const", className: "text-blue-400" }, { text: " [ready] = ", className: "text-white" }, { text: "useState", className: "text-blue-200" }, { text: "(true);", className: "text-purple-400" }],
      [{ text: "  // Delivering fast frontends", className: "text-white/40 italic" }],
      [{ text: "  return", className: "text-blue-400" }, { text: " (", className: "text-white" }],
      [{ text: "    <", className: "text-white" }, { text: "div", className: "text-red-400" }, { text: " className=", className: "text-purple-400" }, { text: '"app"', className: "text-green-400" }, { text: ">", className: "text-white" }],
      [{ text: "      {ready && <", className: "text-white" }, { text: "Hero", className: "text-yellow-200" }, { text: " />}", className: "text-white" }],
      [{ text: "    </", className: "text-white" }, { text: "div", className: "text-red-400" }, { text: ">", className: "text-white" }],
      [{ text: "  );", className: "text-white" }],
      [{ text: "}", className: "text-white" }]
    ]
  }
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getLineCharLength = (lineTokens) =>
  lineTokens.reduce((acc, t) => acc + t.text.length, 0);

const renderLineWithChars = (lineTokens, charIdx) => {
  let remaining = charIdx;
  return lineTokens.map((token, j) => {
    if (remaining <= 0) return null;
    const textToShow = token.text.slice(0, remaining);
    remaining -= token.text.length;
    return (
      <span key={j} className={token.className}>
        {textToShow}
      </span>
    );
  });
};

// ─── CodeSnippet component ────────────────────────────────────────────────────

const CodeSnippet = () => {
  const reduceMotion = useMotionPreference();
  const [snippetIdx, setSnippetIdx] = useState(0);
  const [lineIdx, setLineIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (reduceMotion) return;
    const currentSnippet = SNIPPETS[snippetIdx];
    let timer;

    // Add initial delay for the very first line to allow page to paint
    if (snippetIdx === 0 && lineIdx === 0 && charIdx === 0 && !isDeleting) {
      timer = setTimeout(() => setCharIdx((c) => c + 1), 1500);
      return () => clearTimeout(timer);
    }

    if (isDeleting) {
      if (lineIdx > 0 || charIdx > 0) {
        if (charIdx > 0) {
          timer = setTimeout(() => setCharIdx((c) => c - 1), 15);
        } else {
          const prevLen = getLineCharLength(currentSnippet.lines[lineIdx - 1] || []);
          setLineIdx((l) => l - 1);
          setCharIdx(prevLen);
        }
      } else {
        setIsDeleting(false);
        setSnippetIdx((s) => (s + 1) % SNIPPETS.length);
      }
    } else {
      if (lineIdx < currentSnippet.lines.length) {
        const currentLineTokens = currentSnippet.lines[lineIdx] || [];
        const currentLineLen = getLineCharLength(currentLineTokens);

        if (charIdx < currentLineLen) {
          timer = setTimeout(() => setCharIdx((c) => c + 1), 40 + Math.random() * 30);
        } else {
          timer = setTimeout(() => {
            setLineIdx((l) => l + 1);
            setCharIdx(0);
          }, 200);
        }
      } else {
        timer = setTimeout(() => setIsDeleting(true), 3500);
      }
    }

    return () => clearTimeout(timer);
  }, [lineIdx, charIdx, isDeleting, snippetIdx, reduceMotion]);

  const currentSnippet = SNIPPETS[snippetIdx];
  const displayedLines = reduceMotion ? currentSnippet.lines : currentSnippet.lines.slice(0, lineIdx);
  const currentLineTokens = currentSnippet.lines[lineIdx] || [];

  return (
    <div className="relative aspect-[4/3.2] sm:aspect-[4/5] w-full rounded-2xl bg-[#09090b] border border-white/5 overflow-hidden flex flex-col font-mono shadow-2xl">
      {/* macOS Window Header */}
      <div className="flex items-center gap-2 border-b border-white/5 bg-white/[0.02] px-4 py-3">
        <div className="h-2.5 w-2.5 rounded-full bg-red-500/80" />
        <div className="h-2.5 w-2.5 rounded-full bg-yellow-500/80" />
        <div className="h-2.5 w-2.5 rounded-full bg-green-500/80" />
        <span className="ml-2 text-[10px] sm:text-xs text-white/40">{currentSnippet.title}</span>
      </div>
      {/* Code Content */}
      <div className="flex-1 p-4 sm:p-5 text-white/70 overflow-hidden text-[10px] sm:text-xs leading-[1.8]">
        {displayedLines.map((lineTokens, i) => (
          <div key={i} className="flex">
            <span className="w-6 text-white/20 select-none shrink-0">{i + 1}</span>
            <span className="ml-2 whitespace-pre text-white">
              {lineTokens.map((token, j) => (
                <span key={j} className={token.className}>
                  {token.text}
                </span>
              ))}
            </span>
          </div>
        ))}

        {/* Current typing line */}
        {!reduceMotion && lineIdx < currentSnippet.lines.length && (
          <div className="flex">
            <span className="w-6 text-white/20 select-none shrink-0">{lineIdx + 1}</span>
            <span className="ml-2 whitespace-pre text-white">
              {renderLineWithChars(currentLineTokens, charIdx)}
              <span className="w-2 h-4 bg-brand-400 animate-pulse ml-0.5 inline-block align-middle" />
            </span>
          </div>
        )}

        {/* Blinking cursor when finished typing snippet */}
        {!reduceMotion && lineIdx >= currentSnippet.lines.length && (
          <div className="flex">
            <span className="w-6 text-white/20 select-none shrink-0">{lineIdx + 1}</span>
            <span className="w-2 h-4 bg-brand-400 animate-pulse ml-2 inline-block align-middle" />
          </div>
        )}
      </div>
    </div>
  );
};

export default CodeSnippet;
